import { Link } from "react-router-dom";
import { FilePlus2, Clock, TrendingUp, Users, Printer, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { usePasses } from "../context/PassesContext";
import { useTalmidim } from "../context/TalmidimContext";
import { formatHebrewDate, format12Hour } from "../lib/hebrewDate";
import DigitalClock from "../components/DigitalClock";

export default function Dashboard() {
  const { passes } = usePasses();
  const { klasn } = useTalmidim();
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekAgoStr = weekAgo.toISOString().slice(0, 10);

  const todayPasses = passes.filter((p) => p.date === today);
  const weekPasses = passes.filter((p) => p.date >= weekAgoStr);
  const uniqueTalmidimThisWeek = new Set(weekPasses.map((p) => p.talmidId)).size;

  const perKlass = klasn
    .map((k) => ({
      klas: k,
      count: weekPasses.filter((p) => p.klasId === k.id).length,
    }))
    .filter((k) => k.count > 0)
    .sort((a, b) => b.count - a.count);

  const cards = [
    { label: "Today's Passes", value: todayPasses.length, icon: FilePlus2, color: "blue" },
    { label: "This Week", value: weekPasses.length, icon: Clock, color: "amber" },
    { label: "Unique Students", value: uniqueTalmidimThisWeek, icon: Users, color: "indigo" },
    { label: "All Time", value: passes.length, icon: TrendingUp, color: "emerald" },
  ];

  const colorClasses = {
    blue: "bg-blue-50 text-blue-700",
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
  };

  return (
    <div className="space-y-6">
      <div className="gradient-hero rounded-2xl p-6 lg:p-8 border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <img src="/assets/ohr-hatorah-logo.png" alt="Ohr Hatorah" className="w-24 h-24 lg:w-28 lg:h-28 object-contain drop-shadow-md hidden sm:block" />
            <div>
              <div className="text-xs text-slate-500 font-en tracking-widest mb-2">LATE PASS SYSTEM</div>
              <h1 className="font-serif-heb text-3xl lg:text-4xl font-bold text-slate-900" dir="rtl">רשות כניסה</h1>
              <p className="text-slate-600 mt-2" dir="rtl">{formatHebrewDate(new Date())}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <DigitalClock />
            <div className="flex gap-2">
              <Link to="/new-pass">
                <Button className="gradient-blue-mild text-white">
                  <FilePlus2 className="w-4 h-4 mr-2" />New Late Pass
                </Button>
              </Link>
              <Link to="/history">
                <Button variant="outline">View History</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="card-hover border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm text-slate-500">{c.label}</div>
                    <div className="text-3xl font-bold text-slate-900 mt-2">{c.value}</div>
                  </div>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${colorClasses[c.color]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-serif-heb text-xl">Today's Late Passes</CardTitle>
              <p className="text-xs text-slate-500 mt-1">{todayPasses.length} passes issued today</p>
            </div>
            <Link to="/history" className="text-sm text-blue-700 hover:underline flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
          </CardHeader>
          <CardContent>
            {todayPasses.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <div>No late passes today yet</div>
              </div>
            ) : (
              <div className="space-y-2">
                {todayPasses.slice(0, 8).map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-semibold">{p.talmidName[0]}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800" dir="rtl">{p.talmidName}</div>
                      <div className="text-xs text-slate-500 truncate" dir="rtl">{p.reason} · {p.klasName}</div>
                    </div>
                    <div className="text-sm text-slate-600 font-en">{format12Hour(p.time)}</div>
                    <a href={`/print/${p.id}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline"><Printer className="w-3.5 h-3.5" /></Button>
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-serif-heb text-xl">By Class (This Week)</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Late passes per class</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {perKlass.length === 0 ? (
              <div className="text-slate-400 text-sm text-center py-4">No data</div>
            ) : perKlass.map((k) => (
              <div key={k.klas.id} className="flex items-center justify-between py-1.5">
                <div className="text-sm text-slate-700" dir="rtl">{k.klas.name}</div>
                <Badge variant="secondary">{k.count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
